export const CONTENT_TEMPLATE_IDS = [
  'cifras',
  'historia',
  'zigzag',
  'banner',
  'equipo',
  'testimonios',
  'grilla',
  'preguntas',
  'galeria',
  'valores',
  'llamado',
  'editorial',
  'destino',
  'mapa',
] as const;

export type ContentTemplateId = (typeof CONTENT_TEMPLATE_IDS)[number];

export interface ContentButton {
  texto: string;
  link: string;
  nueva: boolean;
}

export interface ContentLink {
  texto: string;
  link: string;
}

export interface ContentCounter {
  valor: number;
  sufijo: string;
  etiqueta: string;
}

export interface ContentCard {
  icono: string;
  titulo: string;
  texto: string;
  link: ContentLink;
}

export interface ContentRow {
  imagen: string;
  ante: string;
  titulo: string;
  texto: string;
  boton: ContentButton;
}

export interface ContentMember {
  foto: string;
  cargo: string;
  nombre: string;
  desc: string;
}

export interface ContentTestimonial {
  foto: string;
  cita: string;
  nombre: string;
  cargo: string;
}

export interface ContentService {
  imagen: string;
  titulo: string;
  texto: string;
  link: ContentLink;
}

export interface ContentQuestion {
  pregunta: string;
  respuesta: string;
}

export interface ContentValue {
  icono: string;
  titulo: string;
  texto: string;
}

export interface ContentParagraph {
  texto: string;
}

export interface ContentPrice {
  moneda: string;
  valor: number;
  nota: string;
}

export interface ContentDestination {
  nombre: string;
  lat: number;
  lon: number;
  imagen: string;
  texto: string;
  link: ContentLink;
}

export type ContentDatos = Record<string, unknown>;

export const CONTENT_TEMPLATES: Array<{ id: ContentTemplateId; nombre: string; desc: string }> = [
  { id: 'cifras', nombre: 'Cifras de impacto', desc: 'Texto, botón y 4 contadores animados' },
  { id: 'historia', nombre: 'Historia con acciones', desc: 'Texto y 4 tarjetas con ícono' },
  { id: 'zigzag', nombre: 'Zig-zag imagen y texto', desc: '2 filas alternadas de imagen y texto' },
  { id: 'banner', nombre: 'Banner de impacto', desc: 'Foto de fondo, título y 4 tarjetas' },
  { id: 'equipo', nombre: 'Equipo', desc: 'De 2 a 6 integrantes con foto y cargo' },
  { id: 'testimonios', nombre: 'Testimonios', desc: 'Carrusel de 2 a 5 opiniones' },
  { id: 'grilla', nombre: 'Grilla de servicios', desc: '4 fotos grandes con etiqueta de color' },
  { id: 'preguntas', nombre: 'Preguntas frecuentes', desc: 'Acordeón de 2 a 8 preguntas' },
  { id: 'galeria', nombre: 'Galería', desc: '4 fotos escalonadas sobre fondo de dos tonos' },
  { id: 'valores', nombre: 'Valores con íconos', desc: 'De 2 a 6 valores con ícono y texto' },
  { id: 'llamado', nombre: 'Llamado destacado', desc: 'Título, imagen, texto y botón' },
  {
    id: 'editorial',
    nombre: 'Portada editorial',
    desc: 'Palabra gigante calada sobre foto y texto',
  },
  {
    id: 'destino',
    nombre: 'Destino destacado',
    desc: 'Foto completa, precio «desde» y qué incluye',
  },
  { id: 'mapa', nombre: 'Mapa de destinos', desc: 'Mapa con 2 a 8 pines y tarjetas' },
];

const emptyButton = (): ContentButton => ({ texto: '', link: '', nueva: false });
const emptyLink = (): ContentLink => ({ texto: '', link: '' });
export const emptyDestination = (): ContentDestination => ({
  nombre: '',
  lat: 5,
  lon: -25,
  imagen: '',
  texto: '',
  link: { texto: 'Ver paquetes', link: '' },
});
export const emptyTestimonial = (): ContentTestimonial => ({
  foto: '',
  cita: '',
  nombre: '',
  cargo: '',
});

export function isContentTemplateId(value: string): value is ContentTemplateId {
  return (CONTENT_TEMPLATE_IDS as readonly string[]).includes(value);
}

export function emptyDatos(templateId: ContentTemplateId): ContentDatos {
  if (templateId === 'cifras') {
    return {
      titulo: '',
      texto: '',
      boton: emptyButton(),
      imagen: '',
      contadores: [0, 1, 2, 3].map(() => ({ valor: 0, sufijo: '', etiqueta: '' })),
    };
  }
  if (templateId === 'historia') {
    return {
      titulo: '',
      texto: '',
      boton: emptyButton(),
      tarjetas: ['maleta', 'avion', 'asistencia', 'proteccion'].map((icono) => ({
        icono,
        titulo: '',
        texto: '',
        link: emptyLink(),
      })),
    };
  }
  if (templateId === 'zigzag') {
    return {
      invertir: false,
      filas: [0, 1].map(() => ({
        imagen: '',
        ante: '',
        titulo: '',
        texto: '',
        boton: emptyButton(),
      })),
    };
  }
  if (templateId === 'banner') {
    return {
      titulo: '',
      imagen: '',
      tarjetas: ['mundo', 'ubicacion', 'camara', 'estrella'].map((icono) => ({
        icono,
        titulo: '',
        texto: '',
        link: emptyLink(),
      })),
    };
  }
  if (templateId === 'testimonios') {
    return {
      titulo: '',
      imagen: '',
      items: [0, 1].map(emptyTestimonial),
    };
  }
  if (templateId === 'galeria') {
    return { fotos: ['', '', '', ''] };
  }
  if (templateId === 'llamado') {
    return { titulo: '', imagen: '', texto: '', boton: emptyButton() };
  }
  if (templateId === 'mapa') {
    return {
      titulo: '',
      texto: '',
      destinos: [
        { ...emptyDestination(), lat: -34.6, lon: -58.38 },
        { ...emptyDestination(), lat: 40.42, lon: -3.7 },
      ],
    };
  }
  if (templateId === 'destino') {
    return {
      imagen: '',
      antetitulo: '',
      destino: '',
      duracion: '',
      texto: '',
      incluye: ['', '', '', ''],
      precio: { moneda: '', valor: 0, nota: '' },
      boton: emptyButton(),
    };
  }
  if (templateId === 'editorial') {
    return {
      antetitulo: '',
      palabra: '',
      subtitulo: '',
      imagen: '',
      lado: 'izq',
      parrafos: [{ texto: '' }],
      boton: emptyButton(),
    };
  }
  if (templateId === 'valores') {
    return {
      titulo: '',
      valores: ['check', 'mundo'].map((icono) => ({ icono, titulo: '', texto: '' })),
    };
  }
  if (templateId === 'preguntas') {
    return {
      titulo: '',
      texto: '',
      preguntas: [0, 1].map(() => ({ pregunta: '', respuesta: '' })),
    };
  }
  if (templateId === 'grilla') {
    return {
      titulo: '',
      texto: '',
      servicios: [0, 1, 2, 3].map(() => ({ imagen: '', titulo: '', texto: '', link: emptyLink() })),
    };
  }
  return {
    titulo: '',
    integrantes: [0, 1].map(() => ({ foto: '', cargo: '', nombre: '', desc: '' })),
  };
}

export function exampleDatos(templateId: ContentTemplateId): ContentDatos {
  if (templateId === 'cifras') {
    return {
      titulo: 'Cada viaje cuenta',
      texto:
        'Hace más de 20 años acompañamos a agencias y viajeros a descubrir el mundo con la tranquilidad de tener un respaldo real en cada destino.',
      boton: { texto: 'Conocé más', link: '/nosotros', nueva: false },
      imagen: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/0_1792187394767.jpg',
      contadores: [
        { valor: 680, sufijo: '', etiqueta: 'Agencias asociadas' },
        { valor: 103, sufijo: '', etiqueta: 'Destinos activos' },
        { valor: 13, sufijo: '', etiqueta: 'Países' },
        { valor: 9.9, sufijo: 'k', etiqueta: 'Pasajeros por mes' },
      ],
    };
  }
  if (templateId === 'historia') {
    return {
      titulo: 'Nuestra forma de viajar',
      texto:
        'Diseñamos cada experiencia pensando en el pasajero: desde la primera consulta hasta el regreso a casa, con un equipo que está en cada detalle.',
      boton: { texto: 'Ver más', link: '/historia', nueva: false },
      tarjetas: [
        {
          icono: 'maleta',
          titulo: 'Paquetes a medida',
          texto: 'Armamos itinerarios según el perfil y el presupuesto de cada viajero.',
          link: { texto: 'Más', link: '/paquetes' },
        },
        {
          icono: 'avion',
          titulo: 'Aéreos al mejor precio',
          texto: 'Tarifas negociadas con las principales aerolíneas del mundo.',
          link: { texto: 'Más', link: '/aereos' },
        },
        {
          icono: 'asistencia',
          titulo: 'Asistencia 24/7',
          texto: 'Un equipo disponible todos los días ante cualquier imprevisto.',
          link: { texto: 'Más', link: '/asistencia' },
        },
        {
          icono: 'proteccion',
          titulo: 'Viajes protegidos',
          texto: 'Cobertura y respaldo legal en todas nuestras operaciones.',
          link: { texto: 'Más', link: '/proteccion' },
        },
      ],
    };
  }
  if (templateId === 'zigzag') {
    return {
      invertir: false,
      filas: [
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1476514525535-07fb3b4ae5f1_1792188481107.jpeg',
          ante: 'Qué hacemos',
          titulo: 'Experiencias, no solo viajes',
          texto:
            'Seleccionamos alojamientos, excursiones y traslados para que cada día del viaje sea memorable.',
          boton: { texto: 'Ver más', link: '/experiencias', nueva: false },
        },
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1469854523086-cc02fe5d8800_1792188483237.jpeg',
          ante: 'Dónde viajamos',
          titulo: 'América y Europa',
          texto: 'Más de 100 destinos operados con socios locales de confianza en cada región.',
          boton: { texto: 'Ver destinos', link: '/destinos', nueva: false },
        },
      ],
    };
  }
  if (templateId === 'banner') {
    return {
      imagen:
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1506905925346-21bda4d32df4_1792188572646.jpeg',
      titulo: 'Más de 120.000 viajes organizados para 2M de pasajeros en todo el mundo',
      tarjetas: [
        {
          icono: 'mundo',
          titulo: 'Destinos',
          texto: 'Más de 100 destinos en cuatro continentes.',
          link: { texto: '', link: '/destinos' },
        },
        {
          icono: 'ubicacion',
          titulo: 'Excursiones',
          texto: 'Salidas guiadas con operadores locales.',
          link: { texto: '', link: '/excursiones' },
        },
        {
          icono: 'camara',
          titulo: 'Experiencias',
          texto: 'Actividades únicas para recordar siempre.',
          link: { texto: '', link: '/experiencias' },
        },
        {
          icono: 'estrella',
          titulo: 'Calidad premium',
          texto: 'Hoteles y servicios evaluados por nuestro equipo.',
          link: { texto: '', link: '/calidad' },
        },
      ],
    };
  }
  if (templateId === 'testimonios') {
    return {
      titulo: 'Lo que dicen nuestros viajeros',
      imagen:
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1436491865332-7a61a109cc05_1792517294103.jpg',
      items: [
        {
          foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1544005313-94ddf0286df2_1792517296996.jpg',
          cita: 'Todo estuvo perfectamente organizado. Nos sentimos acompañados desde el primer día hasta la vuelta.',
          nombre: 'Valeria Torres',
          cargo: 'Pasajera frecuente',
        },
        {
          foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1472099645785-5658abf4ff4e_1792517295806.jpg',
          cita: 'Como agencia, el respaldo y la rapidez de respuesta del equipo marcan la diferencia con nuestros clientes.',
          nombre: 'Javier Molina',
          cargo: 'Agencia Rumbo Sur',
        },
        {
          foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1438761681033-6461ffad8d80_1792188864840.jpg',
          cita: 'El viaje de egresados fue inolvidable. Excursiones, hoteles y coordinadores de primera.',
          nombre: 'Carla Méndez',
          cargo: 'Viaje de egresados',
        },
      ],
    };
  }
  if (templateId === 'mapa') {
    return {
      titulo: 'Nuestros destinos en el mundo',
      texto: 'Hacé clic en cada punto para conocer las propuestas de la temporada.',
      destinos: [
        {
          nombre: 'Bariloche, Argentina',
          lat: -41.13,
          lon: -71.31,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1470770841072-f978cf4d019e_1792517283135.jpg',
          texto: 'Lagos, montañas y chocolate en el corazón de la Patagonia.',
          link: { texto: 'Ver paquetes', link: '/destinos/bariloche' },
        },
        {
          nombre: 'Río de Janeiro, Brasil',
          lat: -22.91,
          lon: -43.17,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1483729558449-99ef09a8c325_1792517284936.jpg',
          texto: 'Playas icónicas, samba y el Pan de Azúcar al atardecer.',
          link: { texto: 'Ver paquetes', link: '/destinos/rio' },
        },
        {
          nombre: 'Cancún, México',
          lat: 21.16,
          lon: -86.85,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1519046904884-53103b34b206_1792517290455.jpg',
          texto: 'Aguas turquesas y resorts all inclusive frente al Caribe.',
          link: { texto: 'Ver paquetes', link: '/destinos/cancun' },
        },
        {
          nombre: 'Nueva York, EE.UU.',
          lat: 40.71,
          lon: -74,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1496442226666-8d4d0e62e6e9_1792517286742.jpg',
          texto: 'La ciudad que nunca duerme: compras, musicales y rascacielos.',
          link: { texto: 'Ver paquetes', link: '/destinos/nueva-york' },
        },
        {
          nombre: 'Roma, Italia',
          lat: 41.9,
          lon: 12.5,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1552832230-c0197dd311b5_1792517292424.jpg',
          texto: 'Historia milenaria, gastronomía y arte en cada esquina.',
          link: { texto: 'Ver paquetes', link: '/destinos/roma' },
        },
        {
          nombre: 'Tokio, Japón',
          lat: 35.68,
          lon: 139.69,
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1513407030348-c983a97b98d8_1792517288618.jpg',
          texto: 'Tradición y vanguardia en la capital más vibrante de Asia.',
          link: { texto: 'Ver paquetes', link: '/destinos/tokio' },
        },
      ],
    };
  }
  if (templateId === 'destino') {
    return {
      imagen:
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1514282401047-d79a71a590e8_1792517226221.jpg',
      antetitulo: 'Destino destacado',
      destino: 'Maldivas',
      duracion: '9 días / 7 noches',
      texto:
        'Villas sobre el agua, arrecifes de coral y playas de arena blanca. El destino ideal para una luna de miel o unas vacaciones de descanso total.',
      incluye: [
        'Aéreo ida y vuelta',
        'Villa sobre el agua',
        'Traslados en hidroavión',
        'Media pensión',
      ],
      precio: { moneda: 'USD', valor: 3450, nota: 'por persona en base doble' },
      boton: { texto: 'Ver paquete', link: '/paquetes/maldivas', nueva: false },
    };
  }
  if (templateId === 'editorial') {
    return {
      antetitulo: 'Nosotros',
      palabra: 'EXPLORA',
      subtitulo: 'El mundo con nosotros',
      imagen:
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1510414842594-a61c69b5ae57_1792517251531.jpg',
      lado: 'izq',
      parrafos: [
        {
          texto:
            'Somos un grupo de turismo con más de 20 años de trayectoria, dedicado a hacer realidad los viajes que cada pasajero imagina. Lo que empezó como una pequeña agencia hoy es una red que conecta destinos de todo el mundo.',
        },
        {
          texto:
            'Nos guía el compromiso con la atención personalizada y la rapidez en el servicio: acompañamos a cada agencia y a cada viajero desde la primera consulta hasta el regreso.',
        },
        {
          texto:
            'No solo organizamos vacaciones: creamos recuerdos. Te invitamos a descubrir el mundo con nosotros y a vivir la diferencia de viajar con un equipo que se ocupa de cada detalle.',
        },
      ],
      boton: emptyButton(),
    };
  }
  if (templateId === 'llamado') {
    return {
      titulo: 'Sumate a nuestra red de agencias',
      imagen:
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1530789253388-582c481c54b0_1792517281460.jpg',
      texto:
        'Accedé a tarifas exclusivas, capacitaciones y herramientas de venta pensadas para que tu agencia crezca. Te acompañamos en cada paso con un equipo dedicado.',
      boton: { texto: 'Quiero sumarme', link: '/agencias/registro', nueva: false },
    };
  }
  if (templateId === 'valores') {
    return {
      titulo: 'Nuestros valores',
      valores: [
        {
          icono: 'check',
          titulo: 'Vocación de servicio',
          texto:
            'Ponemos al viajero en el centro de cada decisión, antes, durante y después del viaje.',
        },
        {
          icono: 'mundo',
          titulo: 'Sustentabilidad',
          texto: 'Promovemos un turismo responsable con las comunidades locales y el ambiente.',
        },
        {
          icono: 'personas',
          titulo: 'Trabajo en equipo',
          texto: 'Agencias, proveedores y nuestro equipo trabajamos como una sola red.',
        },
        {
          icono: 'proteccion',
          titulo: 'Integridad',
          texto: 'Actuamos con transparencia en cada operación y en cada tarifa.',
        },
      ],
    };
  }
  if (templateId === 'galeria') {
    return {
      fotos: [
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1503220317375-aaad61436b1b_1792517260196.jpg',
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1527631746610-bca00a040d60_1792517262020.jpg',
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1488646953014-85cb44e25828_1792517254899.jpg',
        'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1501785888041-af3ef285b470_1792517257702.jpg',
      ],
    };
  }
  if (templateId === 'preguntas') {
    return {
      titulo: 'Preguntas frecuentes',
      texto: 'Todo lo que necesitás saber antes de viajar.',
      preguntas: [
        {
          pregunta: '¿Qué documentación necesito para viajar?',
          respuesta:
            'Depende del destino: para la mayoría de los países alcanza con el pasaporte vigente. Al reservar te informamos visas y requisitos sanitarios.',
        },
        {
          pregunta: '¿Puedo pagar en cuotas?',
          respuesta:
            'Sí, ofrecemos planes de pago con tarjetas de crédito y transferencias según la promoción vigente.',
        },
        {
          pregunta: '¿Qué incluye la asistencia al viajero?',
          respuesta:
            'Cobertura médica, de equipaje y asistencia 24/7 en todo el mundo, según el plan contratado.',
        },
        {
          pregunta: '¿Puedo modificar la fecha de mi viaje?',
          respuesta:
            'Sí, sujeto a disponibilidad y a las condiciones de cada proveedor. Tu agencia te informa los costos antes de confirmar el cambio.',
        },
      ],
    };
  }
  if (templateId === 'grilla') {
    return {
      titulo: 'Elegí tu próximo destino',
      texto: 'Paquetes pensados para cada tipo de viajero, con salidas confirmadas todo el año.',
      servicios: [
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1512100356356-de1b84283e18_1792517264087.jpg',
          titulo: 'Caribe todo incluido',
          texto: 'Playas de arena blanca y resorts frente al mar.',
          link: { texto: '', link: '/caribe' },
        },
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1533105079780-92b9be482077_1792517278655.jpg',
          titulo: 'Europa clásica',
          texto: 'Ciudades históricas, gastronomía y arte en cada rincón.',
          link: { texto: '', link: '/europa' },
        },
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1528127269322-539801943592_1792517276557.jpg',
          titulo: 'Asia exótica',
          texto: 'Templos, paisajes únicos y culturas milenarias.',
          link: { texto: '', link: '/asia' },
        },
        {
          imagen:
            'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1517760444937-f6397edcbbcd_1792517268456.jpg',
          titulo: 'Aventura y naturaleza',
          texto: 'Trekking, cascadas y parques nacionales.',
          link: { texto: '', link: '/aventura' },
        },
      ],
    };
  }
  return {
    titulo: 'Conocé a nuestro equipo',
    integrantes: [
      {
        foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1494790108377-be9c29b29330_1792188863602.jpg',
        cargo: 'Directora General',
        nombre: 'Laura Gómez',
        desc: 'Lidera la estrategia comercial y la relación con los socios internacionales.',
      },
      {
        foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1507003211169-0a1dd7228f2d_1792188864469.jpg',
        cargo: 'Gerente Comercial',
        nombre: 'Martín Ruiz',
        desc: 'Coordina la red de agencias y el desarrollo de nuevos destinos.',
      },
      {
        foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1438761681033-6461ffad8d80_1792188864840.jpg',
        cargo: 'Líder de Producto',
        nombre: 'Sofía Pérez',
        desc: 'Diseña los paquetes y las experiencias de cada temporada.',
      },
      {
        foto: 'https://mundi-gea-dev.s3.amazonaws.com/marca-blanca/photo-1500648767791-00dcc994a43e_1792188865207.jpg',
        cargo: 'Atención a Agencias',
        nombre: 'Diego Fernández',
        desc: 'Acompaña a cada agencia en sus ventas y consultas diarias.',
      },
    ],
  };
}

function readTitle(templateId: ContentTemplateId, datos: ContentDatos): string {
  if (templateId === 'editorial') return typeof datos.subtitulo === 'string' ? datos.subtitulo : '';
  if (templateId === 'destino') return typeof datos.destino === 'string' ? datos.destino : '';
  if (templateId === 'zigzag') {
    const rows = datos.filas as ContentRow[];
    return rows?.[0]?.titulo || '';
  }
  return typeof datos.titulo === 'string' ? datos.titulo : '';
}

function readText(templateId: ContentTemplateId, datos: ContentDatos): string {
  if (templateId === 'editorial') {
    const paragraphs = datos.parrafos as ContentParagraph[];
    return paragraphs?.[0]?.texto || '';
  }
  if (templateId === 'zigzag') {
    const rows = datos.filas as ContentRow[];
    return rows?.[0]?.texto || '';
  }
  if (['banner', 'equipo', 'testimonios', 'valores'].includes(templateId)) return '';
  return typeof datos.texto === 'string' ? datos.texto : '';
}

export function remapDatos(
  fromId: ContentTemplateId,
  toId: ContentTemplateId,
  datos: ContentDatos,
): ContentDatos {
  const next = emptyDatos(toId);
  const title = readTitle(fromId, datos).trim();
  const body = readText(fromId, datos).trim();
  if (toId === 'zigzag') {
    const rows = next.filas as ContentRow[];
    rows[0] = { ...rows[0], titulo: title, texto: body };
    return next;
  }
  if (toId === 'galeria') return next;
  if (toId === 'destino') {
    next.destino = title;
    next.texto = body;
    return next;
  }
  if (toId === 'editorial') {
    next.subtitulo = title;
    next.parrafos = [{ texto: body }];
    return next;
  }
  next.titulo = title;
  if (['cifras', 'historia', 'grilla', 'preguntas', 'llamado', 'mapa'].includes(toId)) {
    next.texto = body;
  }
  return next;
}

export function isValidContentLink(value: string): boolean {
  if (!value) return false;
  return (
    /^https:\/\/\S+$/i.test(value) ||
    value.startsWith('/') ||
    value.startsWith('#') ||
    /^mailto:\S+$/i.test(value)
  );
}

export function validateDatos(templateId: ContentTemplateId, datos: ContentDatos): string[] {
  const errors: string[] = [];
  if (templateId === 'zigzag') {
    const rows = (datos.filas as ContentRow[]) || [];
    if (!rows[0]?.titulo?.trim()) errors.push('El título de la fila 1 es obligatorio');
    rows.forEach((row, index) => {
      if (row.boton?.texto?.trim() && !isValidContentLink(row.boton.link || '')) {
        errors.push(`El botón de la fila ${index + 1} necesita un link válido`);
      }
    });
    return errors;
  }
  if (templateId === 'galeria') {
    const photos = (datos.fotos as string[]) || [];
    if (photos.length !== 4 || photos.some((photo) => !String(photo || '').trim()))
      errors.push('Las 4 fotos son obligatorias');
    return errors;
  }
  if (templateId === 'destino') {
    if (!String(datos.destino || '').trim()) errors.push('El destino es obligatorio');
    const price = datos.precio as ContentPrice | undefined;
    if (!price || !Number.isFinite(price.valor) || price.valor < 0)
      errors.push('El precio debe ser un número mayor o igual a 0');
    const destinationButton = datos.boton as ContentButton | undefined;
    if (destinationButton?.texto?.trim() && !isValidContentLink(destinationButton.link || '')) {
      errors.push('El botón necesita un link válido');
    }
    return errors;
  }
  if (templateId === 'editorial') {
    if (!String(datos.subtitulo || '').trim()) errors.push('El subtítulo es obligatorio');
    const word = String(datos.palabra || '');
    if (word.length > 10) errors.push('La palabra puede tener hasta 10 letras');
    const paragraphs = (datos.parrafos as ContentParagraph[]) || [];
    if (paragraphs.length < 1 || paragraphs.length > 4)
      errors.push('Los párrafos deben ser entre 1 y 4');
    const editorialButton = datos.boton as ContentButton | undefined;
    if (editorialButton?.texto?.trim() && !isValidContentLink(editorialButton.link || '')) {
      errors.push('El botón necesita un link válido');
    }
    return errors;
  }
  if (!String(datos.titulo || '').trim()) errors.push('El título es obligatorio');
  const button = datos.boton as ContentButton | undefined;
  if (
    (templateId === 'cifras' || templateId === 'historia' || templateId === 'llamado') &&
    button?.texto?.trim() &&
    !isValidContentLink(button.link || '')
  ) {
    errors.push('El botón necesita un link válido');
  }
  if (templateId === 'historia' || templateId === 'banner') {
    const cards = (datos.tarjetas as ContentCard[]) || [];
    cards.forEach((card, index) => {
      if (card.link?.link?.trim() && !isValidContentLink(card.link.link)) {
        errors.push(`El link de la tarjeta ${index + 1} no es válido`);
      }
    });
  }
  if (templateId === 'equipo') {
    const members = (datos.integrantes as ContentMember[]) || [];
    if (members.length < 2 || members.length > 6)
      errors.push('El equipo debe tener entre 2 y 6 integrantes');
  }
  if (templateId === 'grilla') {
    const services = (datos.servicios as ContentService[]) || [];
    services.forEach((service, index) => {
      if (service.link?.link?.trim() && !isValidContentLink(service.link.link)) {
        errors.push(`El link del servicio ${index + 1} no es válido`);
      }
    });
  }
  if (templateId === 'valores') {
    const values = (datos.valores as ContentValue[]) || [];
    if (values.length < 2 || values.length > 6) errors.push('Los valores deben ser entre 2 y 6');
  }
  if (templateId === 'preguntas') {
    const questions = (datos.preguntas as ContentQuestion[]) || [];
    if (questions.length < 2 || questions.length > 8)
      errors.push('Las preguntas deben ser entre 2 y 8');
  }
  if (templateId === 'mapa') {
    const destinations = (datos.destinos as ContentDestination[]) || [];
    if (destinations.length < 2 || destinations.length > 8)
      errors.push('Los destinos deben ser entre 2 y 8');
    destinations.forEach((destination, index) => {
      if (destination.link?.link?.trim() && !isValidContentLink(destination.link.link)) {
        errors.push(`El link del destino ${index + 1} no es válido`);
      }
    });
  }
  if (templateId === 'testimonios') {
    const items = (datos.items as ContentTestimonial[]) || [];
    if (items.length < 2 || items.length > 5) errors.push('Los testimonios deben ser entre 2 y 5');
  }
  return errors;
}
